/**
 * Transport abstraction for the P2P session. A transport connects this peer to
 * every other peer in a room (full mesh) and moves opaque byte messages over
 * two logical channels:
 *   - 'rel'   reliable + ordered   (control, block edits, chat, snapshots)
 *   - 'unrel' unordered, no retransmits (player state, entity state, heartbeats)
 *
 * Implementations: TrysteroTransport (Nostr signaling, zero servers),
 * WorkerTransport (our Cloudflare Worker signaling) and LoopbackHub (in-memory,
 * used by the node smoke tests).
 */

export type Channel = 'rel' | 'unrel';

export interface Transport {
  /** Transport-level id of this peer (not the signed identity id). */
  readonly selfId: string;
  /** Send to one peer, or to every connected peer when `to` is null. */
  send(to: string | null, ch: Channel, data: Uint8Array): void;
  onMessage: ((from: string, ch: Channel, data: Uint8Array) => void) | null;
  onPeerJoin: ((id: string) => void) | null;
  onPeerLeave: ((id: string) => void) | null;
  peers(): string[];
  close(): void;
}

/** In-memory mesh for tests: every node sees every other node. */
export class LoopbackHub {
  private readonly nodes = new Map<string, LoopbackTransport>();
  /** One-way delay in ms (0 = next macrotask). */
  latencyMs = 0;
  /** Probability an 'unrel' message is dropped. */
  unrelLoss = 0;
  /** Pairs that cannot talk (simulated partition), key "a|b". */
  private readonly cut = new Set<string>();

  join(id: string): LoopbackTransport {
    const t = new LoopbackTransport(id, this);
    const existing = [...this.nodes.values()];
    this.nodes.set(id, t);
    for (const o of existing) {
      setTimeout(() => { o.onPeerJoin?.(id); t.onPeerJoin?.(o.selfId); }, 0);
    }
    return t;
  }

  /** Abruptly remove a node (tab closed). */
  kill(id: string): void {
    const t = this.nodes.get(id);
    if (!t) return;
    this.nodes.delete(id);
    t.dead = true;
    for (const o of this.nodes.values()) setTimeout(() => o.onPeerLeave?.(id), 0);
  }

  partition(a: string, b: string, on: boolean): void {
    const k1 = a + '|' + b, k2 = b + '|' + a;
    if (on) { this.cut.add(k1); this.cut.add(k2); } else { this.cut.delete(k1); this.cut.delete(k2); }
  }

  peersOf(id: string): string[] {
    return [...this.nodes.keys()].filter((k) => k !== id && !this.cut.has(id + '|' + k));
  }

  deliver(from: string, to: string | null, ch: Channel, data: Uint8Array): void {
    const targets = to === null ? this.peersOf(from) : [to];
    for (const id of targets) {
      if (this.cut.has(from + '|' + id)) continue;
      if (ch === 'unrel' && this.unrelLoss > 0 && Math.random() < this.unrelLoss) continue;
      const copy = data.slice();
      setTimeout(() => {
        const t = this.nodes.get(id);
        if (t && !t.dead) t.onMessage?.(from, ch, copy);
      }, this.latencyMs);
    }
  }
}

export class LoopbackTransport implements Transport {
  onMessage: ((from: string, ch: Channel, data: Uint8Array) => void) | null = null;
  onPeerJoin: ((id: string) => void) | null = null;
  onPeerLeave: ((id: string) => void) | null = null;
  dead = false;
  constructor(readonly selfId: string, private readonly hub: LoopbackHub) {}
  send(to: string | null, ch: Channel, data: Uint8Array): void {
    if (this.dead) return;
    this.hub.deliver(this.selfId, to, ch, data);
  }
  peers(): string[] { return this.hub.peersOf(this.selfId); }
  close(): void { this.hub.kill(this.selfId); }
}
